/**
 * Order mirrors the `invoice_send_state` pg enum, which IS that type's SQL sort
 * order — so it deliberately differs from SEND_STATE_RANK below. `ORDER BY
 * state` in SQL sorts by this list, not by precedence; never read one as the
 * other.
 */
export const SEND_STATES = [
  'pending', 'accepted', 'rejected', 'delivered', 'bounced', 'complained',
] as const;
export type InvoiceSendState = (typeof SEND_STATES)[number];

/**
 * PRECEDENCE, not sequence (spec D5). A state only ever moves to a HIGHER rank,
 * which is the whole idempotency mechanism: providers retry and reorder, so a
 * duplicate event is a no-op and a late `delivered` cannot overwrite a bounce.
 *
 * This table is the ONLY definition of the ordering, and it is enforced in
 * TypeScript ONLY — unlike LIFECYCLE_RULES, nothing here is emitted as SQL. The
 * webhook decides in TS by calling applySendEvent and then guards its UPDATE
 * with a compare-and-set on the state it read, so the database never needs to
 * know the ranks. The display mapping and the E2E mock call the same function.
 */
export const SEND_STATE_RANK: Record<InvoiceSendState, number> = {
  pending: 0,
  accepted: 1,
  delivered: 2,
  rejected: 3,
  bounced: 4,
  complained: 5,
};

export interface SendEvent {
  type: string;
  /**
   * Resend's data.bounce.type — 'Permanent', 'Transient' or 'Undetermined'.
   * Only a permanent bounce is terminal. Typed as plain `string` because the
   * value is provider text off the wire, not a closed set we control, and it is
   * compared case-insensitively for the same reason.
   */
  bounceType?: string | undefined;
  detail?: string | undefined;
}

/**
 * The outcome of one event. UNIFORM AND ALWAYS RETURNED — there is no null
 * result, and both fields are present-but-nullable.
 *
 * That shape exists for the webhook, which is a single statement:
 *
 *   UPDATE invoice_sends
 *      SET state          = COALESCE($1, state),
 *          outcome_detail = COALESCE($2, outcome_detail)
 *    WHERE id = $3 AND state = $4
 *
 * `$4` is the state the decision was read against; zero rows matched means a
 * concurrent event moved the row first, so re-read it and re-apply the event.
 *
 * A nullable-but-present `detail` is what lets a REFUSED state change still
 * record why: a permanent bounce landing on an already-`complained` row must
 * not move the state, but its reason is new information nobody can recover if
 * it is dropped. Encoding "nothing happened" a second way (a bare null return)
 * would force every caller to handle two spellings of the same fact.
 */
export interface SendEventResult {
  /** null = the precedence rule refused to move the state. */
  state: InvoiceSendState | null;
  /**
   * null = this event carried no detail worth recording.
   *
   * Always WRITTEN when present, but only DISPLAYED behind isBadSendOutcome —
   * see the rule on that function.
   */
  detail: string | null;
}

/**
 * What an incoming provider event does to a row.
 *
 * `state` is non-null only when the event outranks what the row already holds
 * (see SEND_STATE_RANK). `detail` rides along independently of that decision.
 */
export function applySendEvent(
  current: InvoiceSendState,
  event: SendEvent,
): SendEventResult {
  const detail = event.detail ?? null;
  const advance = (next: InvoiceSendState): SendEventResult =>
    SEND_STATE_RANK[next] > SEND_STATE_RANK[current]
      ? { state: next, detail }
      : { state: null, detail };

  switch (event.type) {
    case 'email.sent':      return advance('accepted');
    case 'email.delivered': return advance('delivered');
    case 'email.complained':return advance('complained');
    case 'email.failed':    return advance('rejected');
    case 'email.bounced':
      // A full mailbox or a greylisting AP server bounces transiently and then
      // delivers. Making that terminal leaves a destructive badge on an invoice
      // the client actually received. The reason is still worth recording.
      //
      // Case-insensitive: the casing is the provider's, and reading a lowercase
      // 'permanent' as non-permanent would quietly downgrade every hard bounce.
      return event.bounceType?.toLowerCase() === 'permanent'
        ? advance('bounced')
        : { state: null, detail };
    case 'email.delivery_delayed':
      return { state: null, detail };
    default:
      // An event type we do not model (email.opened, email.clicked). We refuse
      // its detail too: recording text from a rule we have not written would
      // put unexplained strings on the row.
      return { state: null, detail: null };
  }
}

/** An accepted row with no confirmation after this long is reported, not assumed good (D10). */
export const DELIVERY_CONFIRMATION_GRACE_MS = 24 * 3600_000;
/** A pending row older than this was interrupted mid-send, and is displayed as unknown. */
export const SEND_PENDING_GRACE_MS = 5 * 60_000;

/*
 * The double-click guard that used to live here (SEND_IN_PROGRESS_MS) has moved
 * to backend/src/services/invoice-send.service.ts. It has to exceed one whole
 * PDF render, and PDF_TIMEOUT_MS is a backend constant this package cannot see;
 * a hard-coded 60s here was a second, wrong copy of a number it could not
 * derive. Nothing in the frontend consumed it — the guard is enforced server-
 * side by a partial unique index, and the client only ever renders the outcome.
 */

/**
 * The label for a state that does not depend on how long the row has been
 * sitting there. Every BAD outcome is in here, which is why the row badge — a
 * surface that has a state but no send timestamp — can call this and nothing
 * else.
 *
 * Keeping it separate from sendOutcomeLabel is a type-level guard, not tidiness:
 * the badge previously passed an inert epoch to satisfy a `createdAt` parameter
 * it had no value for. That was correct only by luck (the two age-dependent
 * branches are states the badge never renders) and would have become a silent
 * lie the day a bad state grew an age-dependent label.
 */
export function sendStateLabel(state: InvoiceSendState): string | null {
  switch (state) {
    case 'delivered': return 'Delivered';
    case 'bounced':   return 'Bounced';
    case 'complained':return 'Marked as spam';
    case 'rejected':  return 'Failed';
    // Age-dependent — the caller must use sendOutcomeLabel, which has a clock.
    case 'pending':
    case 'accepted':  return null;
  }
}

/**
 * The full label, including the two states whose reading depends on age: a
 * `pending` row past its grace is "Outcome unknown", and an `accepted` row with
 * no confirmation after 24h is "No delivery confirmation" (spec D10). Callers
 * that hold a whole send row use this; pass `now` explicitly in tests.
 */
export function sendOutcomeLabel(
  send: { state: InvoiceSendState; createdAt: string },
  now: number = Date.now(),
): string {
  const age = now - new Date(send.createdAt).getTime();
  switch (send.state) {
    case 'pending':   return age > SEND_PENDING_GRACE_MS ? 'Outcome unknown' : 'Sending…';
    case 'accepted':  return age > DELIVERY_CONFIRMATION_GRACE_MS ? 'No delivery confirmation' : 'Sent';
    default:          return sendStateLabel(send.state)!;
  }
}

/**
 * Rows the UI must call out in red — AND the gate on showing `outcomeDetail`
 * at all.
 *
 * A transient bounce writes "mailbox full", and the `email.delivered` that
 * follows it cannot clear that string: the precedence rule moves the state to
 * delivered but has no business erasing history. So a row can read Delivered
 * while still carrying a failure detail, and showing it in the badge tooltip
 * would contradict the badge. Display the detail only when this returns true.
 * The write is never gated — only the display is.
 */
export const isBadSendOutcome = (s: InvoiceSendState): boolean =>
  s === 'bounced' || s === 'complained' || s === 'rejected';
